import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { useApiMutation } from '../../../shared/useApi.js';

const SALES_KEY = ['pos', 'my-sales'];

function dayStartISO() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * فواتير اليوم المسجّلة باسم المستخدم نفسه.
 *
 * `mine=1` يفرضه الخادم على معرّف المستخدم في الجلسة (لا يُمرَّر cashierId من
 * الواجهة)، فالكاشير يرى فواتيره فقط ولا يستطيع إلغاء فاتورة زميله من هنا.
 */
export function useMySalesToday({ enabled = true } = {}) {
  return useQuery({
    queryKey: [...SALES_KEY, 'today'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get('/pos/sales', {
        params: { mine: 1, from: dayStartISO(), to: new Date().toISOString(), limit: 100 }
      });
      return Array.isArray(data) ? data : data?.items || [];
    },
    refetchInterval: enabled ? 30_000 : false,
    staleTime: 5_000
  });
}

/**
 * إلغاء فاتورة بيع.
 *
 * الخادم (`pos/services/invoices.cancelInvoice`) هو من يفعل كل شيء:
 * يبقي الفاتورة في القاعدة بحالة `cancelled`، يعيد الكميات للمخزون، يُخرج
 * النقد المُعاد من درج الشفت المفتوح بـ requireFunds، يعيد رصيد الكارت،
 * يرفض الإلغاء مرتين، ويكتب الحدث في سجلّ التدقيق باسم من ألغى والسبب.
 * الواجهة لا تكرّر أياً من ذلك — ترسل السبب وتعرض النتيجة.
 */
export function useCancelInvoice() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: ({ id, reason }) =>
      api.post(`/pos/sales/${id}/cancel`, { reason }).then((r) => r.data),
    // رسالة النجاح تُعرض في النافذة نفسها مع رقم الفاتورة.
    errorToast: false,
    onSuccess: async () => {
      // المخزون والقاصة والجلسة تغيّرت فعلاً — أعِد جلب ما يعرضها الآن.
      await qc.invalidateQueries({ queryKey: SALES_KEY, refetchType: 'active' });
      qc.invalidateQueries({ queryKey: ['pos', 'products'] });
      qc.invalidateQueries({ queryKey: ['sessions', 'active'] });
      qc.invalidateQueries({ queryKey: ['cashboxes'] });
      qc.invalidateQueries({ queryKey: ['shift'] });
      qc.invalidateQueries({ queryKey: ['cards'] });
      qc.invalidateQueries({ queryKey: ['debts'] });
    }
  });
}
